/**
 * The permissions the role and channel editors show, in Discord's groups,
 * each with a label and a one-line description (EN and FA in i18n/en.ts and
 * i18n/fa.ts: `ns.perm.<KEY>`, `ns.permd.<KEY>`).
 *
 * Only permissions that do something here are listed (a toggle that does
 * nothing is a bug). Not listed, kept in the bitfield untouched:
 * - Discord bits whose feature we don't have yet: Use Voice Activity
 *   (forced push-to-talk is not built), Embed
 *   Links (no link previews), Bypass Slowmode (slowmode can't be set in the
 *   app yet), events, stickers, soundboard, webhooks, text to
 *   speech, external emoji and apps, insights, monetisation, expressions,
 *   application commands, request to speak, voice
 *   messages;
 * - our nx bits of the removed Comms mode (Whisper, Channel commander;
 *   `COMMS_ONLY_NX_PERMISSIONS`), and talk power, retired in Hub (a role's
 *   and a channel's stay stored, but decide nothing:
 *   docs/decisions/2026-10-28-talk-power-retired.md);
 * - Set voice region (until a second region exists, voice-regions §2.4);
 * - the retired clan-leader bit (`RESERVED_NX_PERMISSIONS`), which no longer
 *   exists as a permission at all.
 *
 * "Clear permissions" and every editor change only the bits listed here.
 */
import {
  NxPermissions,
  Permissions,
  type NxPermissionName,
  type PermissionName,
} from '@neosocial/protocol';

/** Where a permission applies: a text channel, a voice channel (with its chat), or the server only. */
export type PermScope = 'text' | 'voice' | 'server';

export interface PermDef {
  key: PermissionName | NxPermissionName;
  nx?: boolean;
  bit: bigint;
  /** i18n key for the label. */
  label: string;
  /** i18n key for the one-line description. */
  desc: string;
  /** Channel types where it can be set as an overwrite; `server` = roles only. */
  scope: PermScope[];
  /** Administrator: shown with a warning. */
  danger?: boolean;
}

const TV: PermScope[] = ['text', 'voice'];
const V: PermScope[] = ['voice'];
const S: PermScope[] = ['server'];
/** Threads live in text channels (2026-10-29). */
const T: PermScope[] = ['text'];

const p = (key: PermissionName, scope: PermScope[], danger = false): PermDef => ({
  key,
  bit: Permissions[key],
  label: `ns.perm.${key}`,
  desc: `ns.permd.${key}`,
  scope,
  ...(danger ? { danger } : {}),
});
const n = (key: NxPermissionName, scope: PermScope[]): PermDef => ({
  key,
  nx: true,
  bit: NxPermissions[key],
  label: `ns.perm.${key}`,
  desc: `ns.permd.${key}`,
  scope,
});

export interface PermGroup {
  /** i18n key for the group title. */
  title: string;
  perms: PermDef[];
}

/** Discord's groups: General, Membership, Text, Voice, Advanced. */
export const ROLE_PERM_GROUPS: PermGroup[] = [
  {
    title: 'ns.perm.g.general',
    perms: [
      p('VIEW_CHANNEL', TV),
      p('MANAGE_CHANNELS', TV),
      p('MANAGE_ROLES', TV),
      p('MANAGE_GUILD', S),
      p('VIEW_AUDIT_LOG', S),
      // custom emoji (2026-11-01), Discord's "Create expressions" and "Manage expressions"
      p('CREATE_GUILD_EXPRESSIONS', S),
      p('MANAGE_GUILD_EXPRESSIONS', S),
      // scheduled events (2026-10-29): per voice or stage channel, or server-wide
      p('CREATE_EVENTS', V),
      p('MANAGE_EVENTS', V),
    ],
  },
  {
    title: 'ns.perm.g.membership',
    perms: [
      p('CREATE_INSTANT_INVITE', TV),
      p('CHANGE_NICKNAME', S),
      p('MANAGE_NICKNAMES', S),
      p('KICK_MEMBERS', S),
      p('BAN_MEMBERS', S),
      p('MODERATE_MEMBERS', S),
      n('POKE', S),
    ],
  },
  {
    title: 'ns.perm.g.text',
    perms: [
      p('SEND_MESSAGES', TV),
      p('ATTACH_FILES', TV),
      p('ADD_REACTIONS', TV),
      p('MENTION_EVERYONE', TV),
      p('MANAGE_MESSAGES', TV),
      p('PIN_MESSAGES', TV),
      p('SEND_POLLS', TV),
      p('READ_MESSAGE_HISTORY', TV),
      // threads (2026-10-29): Discord's four
      p('CREATE_PUBLIC_THREADS', T),
      p('CREATE_PRIVATE_THREADS', T),
      p('SEND_MESSAGES_IN_THREADS', T),
      p('MANAGE_THREADS', T),
    ],
  },
  {
    title: 'ns.perm.g.voice',
    perms: [
      p('CONNECT', V),
      p('SPEAK', V),
      // Discord's "Video": screen share and camera (2026-10-29-screen-share-and-video.md)
      p('STREAM', V),
      // the voice extras (2026-11-01)
      p('PRIORITY_SPEAKER', V),
      p('SET_VOICE_CHANNEL_STATUS', V),
      p('MUTE_MEMBERS', V),
      p('DEAFEN_MEMBERS', V),
      p('MOVE_MEMBERS', V),
      n('BYPASS_CHANNEL_PASSWORD', V),
    ],
  },
  {
    title: 'ns.perm.g.advanced',
    perms: [p('ADMINISTRATOR', S, true)],
  },
];

/** Every permission the editors show. */
export const ALL_PERM_DEFS: PermDef[] = ROLE_PERM_GROUPS.flatMap((g) => g.perms);

/** The Discord bits and the nx bits the editors may change ("Clear permissions" clears these). */
export const EDITABLE_BITS = ALL_PERM_DEFS.filter((d) => !d.nx).reduce((a, d) => a | d.bit, 0n);
export const EDITABLE_NX_BITS = ALL_PERM_DEFS.filter((d) => d.nx).reduce((a, d) => a | d.bit, 0n);

/** The channel kinds the overwrite editor knows. */
export type ChannelKind = 'text' | 'voice' | 'category';

/**
 * The groups a channel's overwrite editor shows, as Discord does: a text
 * channel gets the text permissions, a voice channel the voice ones plus its
 * chat's, a category everything a channel can take. Server-only permissions
 * never appear in overwrites.
 */
export function overwriteGroups(kind: ChannelKind): PermGroup[] {
  const ok = (d: PermDef) =>
    kind === 'category'
      ? !d.scope.includes('server')
      : d.scope.includes(kind) || (kind === 'voice' && d.scope.includes('text'));
  return ROLE_PERM_GROUPS.map((g) => ({ title: g.title, perms: g.perms.filter(ok) })).filter(
    (g) => g.perms.length > 0,
  );
}

/** The flat list for a channel's overwrites (see `overwriteGroups`). */
export function overwritePerms(kind: ChannelKind): PermDef[] {
  return overwriteGroups(kind).flatMap((g) => g.perms);
}
